import { useState, useMemo, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import RequirePagePermission from '../RequirePagePermission'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  LineChartOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  SafetyOutlined,
  IdcardOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import { Layout, Menu, Button, theme, Avatar, Dropdown } from 'antd'
import HeaderSearch from '../HeaderSearch'
import type { MenuProps } from 'antd'
import { MENU_CODE_TO_PATH } from '../../services/api'
import type { MenuTreeNode } from '../../contexts/AuthContext'
import logoImg from '@assets/logo.png'

const { Header, Sider, Content } = Layout

const ICON_BY_CODE: Record<string, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  asset_products: <AppstoreOutlined />,
  data_sources: <BarChartOutlined />,
  analytics: <LineChartOutlined />,
  settings: <SettingOutlined />,
  settings_profile: <UserOutlined />,
  settings_security: <SafetyOutlined />,
  user_management: <TeamOutlined />,
  role_management: <IdcardOutlined />,
  accounts: <UserOutlined />,
  roles: <IdcardOutlined />,
  export_logs: <DownloadOutlined />,
  'sub-system': <TeamOutlined />,
}

/** 前端 path 对应菜单 code（与接口 menuTree 的 code 一致） */
const pathToKey: Record<string, string> = {
  '/': 'dashboard',
  '/data': 'data_sources',
  '/assets': 'asset_products',
  '/analytics': 'analytics',
  '/settings': 'settings_profile',
  '/settings/profile': 'settings_profile',
  '/settings/security': 'settings_security',
  '/roles': 'role_management',
  '/accounts': 'user_management',
  '/export-logs': 'export_logs',
}

const keyToPath: Record<string, string> = {
  dashboard: '/',
  data_sources: '/data',
  asset_products: '/assets',
  analytics: '/analytics',
  settings: '/settings',
  settings_profile: '/settings/profile',
  settings_security: '/settings/security',
  user_management: '/accounts',
  role_management: '/roles',
  accounts: '/accounts',
  roles: '/roles',
  export_logs: '/export-logs',
}

function buildMenuItemsFromTree(tree: MenuTreeNode[]): NonNullable<MenuProps['items']> {
  const items = tree
    .filter((node) => node.type === 'menu')
    .map((node) => {
      const children = node.children?.length
        ? buildMenuItemsFromTree(node.children)
        : undefined
      return {
        key: node.code,
        icon: ICON_BY_CODE[node.code] ?? null,
        label: node.name,
        children: children?.length ? children : undefined,
      }
    })
  return items
}

const LayoutComponent = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, hasPermission, menuTree } = useAuth()
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken()

  const selectedKey = useMemo(() => {
    const path = location.pathname
    // 详情页等带参数路由做前缀匹配
    if (path.startsWith('/assets/')) return 'asset_products'
    if (path.startsWith('/data')) return 'data_sources'
    if (path.startsWith('/analytics')) return 'analytics'
    if (path.startsWith('/settings')) {
      if (path.startsWith('/settings/security')) return 'settings_security'
      return 'settings_profile'
    }
    if (path.startsWith('/accounts')) return 'user_management'
    if (path.startsWith('/roles')) return 'role_management'
    if (path.startsWith('/export-logs')) return 'export_logs'
    return pathToKey[path] ?? 'dashboard'
  }, [location.pathname])

  useEffect(() => {
    if (['settings_profile', 'settings_security', 'user_management', 'role_management', 'export_logs'].includes(selectedKey)) {
      setOpenKeys((prev) => (prev.includes('settings') ? prev : ['settings']))
    }
  }, [selectedKey])

  const showDashboard = hasPermission('dashboard:summary') || hasPermission('dashboard:distribution') || hasPermission('dashboard:recent_activities')
  const showDataSources = hasPermission('data_sources:list') || hasPermission('data_sources:summary')
  const showAssetProducts = hasPermission('asset_products:list') || hasPermission('asset_products:detail')
  const showAnalytics =
    hasPermission('analytics:indicators') ||
    hasPermission('analytics:reports_list') ||
    hasPermission('analytics:asset_distribution') ||
    hasPermission('analytics:trend_analysis') ||
    hasPermission('analytics:comparison_analysis') ||
    hasPermission('analytics:amount_dimension')
  const showSettings =
    hasPermission('settings:profile_get') ||
    hasPermission('settings:system_get') ||
    hasPermission('settings:notifications_get') ||
    hasPermission('settings:security_get')
  const showUserManage = hasPermission('user_management:list')
  const showRoleManage = hasPermission('role_management:list')
  const showExportLogs = hasPermission('export_logs:list') || hasPermission('data:export')

  const menuItems: MenuProps['items'] = useMemo(() => {
    if (menuTree.length > 0) {
      return buildMenuItemsFromTree(menuTree)
    }
    const settingsChildren: MenuProps['items'] = []
    if (showSettings) {
      settingsChildren.push(
        { key: 'settings_profile', icon: <UserOutlined />, label: '个人资料' },
        { key: 'settings_security', icon: <SafetyOutlined />, label: '安全设置' }
      )
      if (showUserManage) settingsChildren.push({ key: 'user_management', icon: <TeamOutlined />, label: '用户管理' })
      if (showRoleManage) settingsChildren.push({ key: 'role_management', icon: <IdcardOutlined />, label: '角色管理' })
      if (showExportLogs) settingsChildren.push({ key: 'export_logs', icon: <DownloadOutlined />, label: '日志管理' })
    }
    const items: MenuProps['items'] = []
    if (showDashboard) items.push({ key: 'dashboard', icon: <DashboardOutlined />, label: '仪表板' })
    if (showDataSources) items.push({ key: 'data_sources', icon: <BarChartOutlined />, label: '资产数据' })
    if (showAssetProducts) items.push({ key: 'asset_products', icon: <AppstoreOutlined />, label: '资产产品' })
    if (showAnalytics) items.push({ key: 'analytics', icon: <LineChartOutlined />, label: '分析报告' })
    if (showSettings && settingsChildren.length > 0) items.push({ key: 'settings', icon: <SettingOutlined />, label: '系统设置', children: settingsChildren })
    return items
  }, [menuTree, showDashboard, showDataSources, showExportLogs, showAssetProducts, showAnalytics, showSettings, showUserManage, showRoleManage])

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const path = MENU_CODE_TO_PATH[key as string] ?? keyToPath[key as string]
    if (path) navigate(path)
    if (
      !['settings', 'settings_profile', 'settings_security', 'user_management', 'role_management', 'export_logs'].includes(
        key as string,
      )
    ) {
      setOpenKeys([])
    }
  }

  const canAccessSettings =
    hasPermission('settings:profile_get') ||
    hasPermission('settings:system_get') ||
    hasPermission('settings:notifications_get') ||
    hasPermission('settings:security_get')

  const userMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = []
    if (canAccessSettings) {
      items.push(
        { key: 'profile', label: '个人资料', icon: <UserOutlined /> },
        { key: 'settings', label: '账户设置', icon: <SettingOutlined /> },
        { type: 'divider' as const }
      )
    }
    items.push({ key: 'logout', label: '退出登录', icon: <LogoutOutlined /> })
    return items
  }, [canAccessSettings])

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      logout()
      navigate('/login', { replace: true })
      return
    }
    if (key === 'settings') navigate('/settings/profile')
    if (key === 'profile') navigate('/settings/profile')
  }

  return (
    <Layout className="min-h-screen">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={256}
        style={{
          background: colorBgContainer,
          borderRight: '1px solid #f0f0f0',
        }}
      >
        <div className="flex items-center justify-center h-16 border-b border-gray-200 dark:border-gray-700 px-3">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={logoImg}
              alt="数字资产管理系统"
              className={`shrink-0 object-contain rounded-lg ${collapsed ? 'h-8 w-8' : 'h-10 w-10'}`}
            />
            {!collapsed && (
              <div>
                <div className="font-bold text-lg text-gray-900 dark:text-gray-100">数字资产管理系统</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">统一数字资产管理平台</div>
              </div>
            )}
          </div>
        </div>
        
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys.map(String))}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
          className="mt-4"
        />
        
        {!collapsed && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
            <div className="text-xs text-gray-500 mb-2">版本 v1.2.1</div>
            <div className="text-xs text-gray-400">© 2026 数字资产管理系统</div>
          </div>
        )}
      </Sider>
      
      <Layout>
        <Header
          style={{
            padding: '0 24px',
            background: colorBgContainer,
            borderBottom: '1px solid #f0f0f0',
          }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center space-x-4">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px' }}
            />
            
            <HeaderSearch />
          </div>
          
          <div className="flex items-center space-x-4">
            {/* <Badge count={5} size="small">
              <Button
                type="text"
                icon={<BellOutlined style={{ fontSize: '18px' }} />}
                shape="circle"
              />
            </Badge> */}
            
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-3 py-1 rounded-lg">
                <Avatar
                  size="default"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#3b82f6' }}
                />
                {!collapsed && (
                  <div className="hidden md:flex flex-col items-start justify-center">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {user?.name ?? user?.nickname ?? user?.phone ?? user?.username ?? '用户'}
                    </span>
                    {/* <span className="text-xs text-gray-500">管理员</span> */}
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>
        
        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            overflow: 'auto',
          }}
        >
          <RequirePagePermission>
            <Outlet />
          </RequirePagePermission>
        </Content>
      </Layout>
    </Layout>
  )
}

export default LayoutComponent